import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import './NotificationSystem.css';
import { Close } from '@mui/icons-material';

export const NOTIFICATION = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
};

const NotificationContext = createContext({ showNotification: () => { } });

const DURATION = 5000;
const FADE_DURATION = 100;

export const useNotification = () => useContext(NotificationContext);

// Decorative: the notification text next to the icon already states the outcome,
// so an alt would only repeat it — untranslated — to a screen reader.
const getNotificationIcon = (type) => {
    if (type === NOTIFICATION.SUCCESS) return <img src="/img/ok.png" alt="" aria-hidden="true" className="notification-icon" />;
    if (type === NOTIFICATION.ERROR) return <img src="/img/error.png" alt="" aria-hidden="true" className="notification-icon" />;
    if (type === NOTIFICATION.WARNING) return <img src="/img/warning.png" alt="" aria-hidden="true" className="notification-icon" />;
    // info, warning, or default
    return <img src="/img/info.png" alt="" aria-hidden="true" className="notification-icon" />;
};

const NotificationBar = ({ notification, onClose, fadeOut, animKey }) => {
    if (!notification) return null;
    const action = notification.action;
    return (
        <div key={animKey} className={`notification-bar ${notification.type || ''}${fadeOut ? ' fade-out' : ''}`}>
            {getNotificationIcon(notification.type)}
            <span className='notification-message'>{notification.message}</span>
            {action && (
                <button className="notification-action" onClick={action.onClick}>{action.label}</button>
            )}
            <button className="notification-close" onClick={onClose}><Close fontSize='small' /></button>
        </div>
    );
};

export const NotificationProvider = ({ children }) => {
    const [notification, setNotification] = useState(null);
    const [fadeOut, setFadeOut] = useState(false);
    const [animKey, setAnimKey] = useState(0);
    const timeoutRef = useRef();
    const fadeTimeoutRef = useRef();

    const clearAllTimeouts = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    }, []);

    const removeNotification = useCallback(() => {
        setNotification(null);
        setFadeOut(false);
    }, []);

    const startFadeOut = useCallback(() => {
        setFadeOut(true);
        fadeTimeoutRef.current = setTimeout(removeNotification, FADE_DURATION);
    }, [removeNotification]);

    /**
     * `action` renders a button beside the message: `{ label, onClick }`.
     * `duration` of 0 keeps the bar up until the user acts or closes it, which
     * is what an actionable notice needs — a five second offer to reload is one
     * the user will mostly watch disappear.
     */
    const showNotification = useCallback((message, type = '', duration = DURATION, action = null) => {
        clearAllTimeouts();
        setFadeOut(false);
        setNotification({ message, type, action });
        setAnimKey(prev => prev + 1); // force re-mount for fade-in
        if (duration > 0) {
            timeoutRef.current = setTimeout(() => {
                startFadeOut();
            }, duration);
        }
    }, [clearAllTimeouts, startFadeOut]);

    const handleClose = () => {
        clearAllTimeouts();
        startFadeOut();
    };

    return (
        <NotificationContext.Provider value={{ showNotification }}>
            <NotificationBar notification={notification} onClose={handleClose} fadeOut={fadeOut} animKey={animKey} />
            {children}
        </NotificationContext.Provider>
    );
}; 
